/**
 * Single source of truth for the reorder note shown on the order timeline.
 *
 * AB#20682. Two defects lived in this logic, duplicated across four repos:
 *
 *  1. When the referenced order had no `order_number`, the renderer printed
 *     the raw `orders.id` behind a `#`, which is indistinguishable from a
 *     customer-facing order number. Support searched the id as an order
 *     number and landed on an unrelated customer's order. `orders.order_no`
 *     is NULL by design until Mark to Factory, so this fired on any
 *     early-lifecycle reference.
 *
 *  2. The "(order not confirmed)" suffix was driven by the CURRENT order's
 *     `order_no` instead of the REFERENCED order's `order_number`, so it was
 *     suppressed exactly when it was needed.
 *
 * This module previously existed as two copies inside admin_v2 alone
 * (a local helper in OrderTimeline.vue and another in useProductDetails.ts)
 * which had already drifted apart on defect 2. Keep it here and import it.
 */

export interface ReorderData {
  order_number?: string | number | null
  order_id?: string | number | null
  design_change?: boolean
  roster_change?: boolean
}

export interface ReorderFactoryProduct {
  reorder_data?: ReorderData | null
  is_possible_reorder?: boolean
}

/**
 * How the previous order is referred to in the note.
 *
 * Never returns a bare `#` + primary key. When no order number exists yet the
 * reference stays deliberately vague — the "(order not confirmed)" suffix
 * carries the detail.
 */
const describeReference = (reorder_data: ReorderData): string =>
  reorder_data.order_number ? `order #${reorder_data.order_number}` : 'an earlier order'

const describeChanges = (reorder_data: ReorderData): string => {
  if (reorder_data.roster_change === true && reorder_data.design_change === true) {
    return 'with design and roster changes.'
  }
  if (reorder_data.roster_change === true && reorder_data.design_change === false) {
    return 'with roster changes.'
  }
  if (reorder_data.roster_change === false && reorder_data.design_change === true) {
    return 'with design changes.'
  }
  return 'with no modifications'
}

export const makeReorderMessage = (factory_product?: ReorderFactoryProduct | null): string => {
  if (!factory_product || !factory_product.reorder_data) {
    return ''
  }

  const reorder_data = factory_product.reorder_data
  const isPossible = Boolean(factory_product.is_possible_reorder)

  let message = isPossible
    ? `Note: Possible reorder of ${describeReference(reorder_data)} `
    : `Note: Reorder of ${describeReference(reorder_data)} `

  message += describeChanges(reorder_data)

  // Driven by the REFERENCED order, not the current one — that inversion was
  // defect 2. Applies to both branches.
  if (!reorder_data.order_number) {
    message += ' (order not confirmed)'
  }

  return message
}
